<template>
  <AsidePanel
    ref="asidePanel"
    cancelText="close"
    :components="components"
    :handleDelete="handleDelete"
    :handleSave="handleSave"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :redirectTo="redirectTo"
    :showDeleteButton="true"
    :viewport="viewport"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="windows"
      />
    </template>
  </AsidePanel>
</template>
<script>
import {
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import AdBridgeAgentItemTable from 'client/directories/ActiveDirectory/AdBridgeAgentItemTable.vue';
import AdBridgeDetails from 'client/directories/ActiveDirectory/AdBridgeDetails.vue';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import DeleteAdDomainModal from 'client/directories/ActiveDirectory/DeleteAdDomainModal.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import SystemUserItemTable from 'client/systemusers/SystemUserItemTable/SystemUserItemTable.vue';
import UserGroupItemTable from 'client/components/ItemTable/UserGroupItemTable.vue';

const { create, update } = ItemActionConsts;
const {
  activeDirectory,
  adBridgeAgent,
  directory,
  systemUser,
  userGroup,
} = ItemTypeConsts;
const storeModel = StoreModelMap[activeDirectory];
const directoryCollection = StoreModuleMap[directory];

export default {
  name: 'AdBridgeAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
  },

  mixins: [
    AsidePanelPropsMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: activeDirectory,
      required: false,
    },
  },

  data() {
    const components = [{
      label: 'Details',
      vueComponent: AdBridgeDetails,
    }];
    return {
      activeDirectoryRoute: ItemTypeRoutes[activeDirectory],
      components,
      itemHasBeenSaved: false,
    };
  },

  computed: {
    ...mapState(storeModel, {
      numberOfImportAgents: state => state.item.numberOfImportAgents,
    }),

    redirectTo() {
      return this.activeDirectoryRoute;
    },
  },

  watch: {
    /**
     * The number of import agents bound to this AD Bridge domain isn't known
     * when the data is populated. So a watch is set on the numberOfImportAgents
     * computed property. When the value changes and is greater than 0, the
     * AdBridgeAgentItemTable will be rendered.
     *
     * Using a watch could be avoided if the tab could be rendered when no
     * agents are present, but this is not the current behavior!
     */
    numberOfImportAgents(newValue) {
      if (newValue > 0) {
        this.components.push({
          itemType: adBridgeAgent,
          label: 'Domain Agents',
          headerLabels: {
            [create]: `The following ${ItemTypeDisplayNames[adBridgeAgent]}s are available`,
            [update]: `has the following ${ItemTypeDisplayNames[adBridgeAgent]}s available`,
          },
          vueComponent: AdBridgeAgentItemTable,
        });
        const userGroupHeaderLabel = 'Members of the following user groups are syncing to the Active Directory domain';

        this.components.push({
          label: 'User Groups',
          headerLabels: {
            [create]: userGroupHeaderLabel,
            [update]: userGroupHeaderLabel,
            useDisplayName: false,
          },
          itemType: ItemTypeConsts.userGroup,
          vueComponent: UserGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId ? `/api/v2/activedirectories/${this.itemId}/usergroups` : undefined,
            inDetailsAsidePanel: true,
          },
        });

        const headerLabel = `The following ${ItemTypeDisplayNames[systemUser]}s are syncing to this ${ItemTypeDisplayNames[activeDirectory]}`;

        this.components.push({
          itemType: systemUser,
          label: 'Users',
          headerLabels: {
            [create]: headerLabel,
            [update]: headerLabel,
          },
          vueComponent: SystemUserItemTable,
          vueComponentProps: {
            boundItemsUrl: `/api/v2/activedirectories/${this.itemId}/users`,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.adDomainAsidePanel,
          },
        });
      }
    },
  },

  methods: {
    ...mapGetters(storeModel, [
      'haveAssociationsChanged',
    ]),

    ...mapActions(directoryCollection, [
      'refreshCollection',
    ]),

    ...mapActions(storeModel, [
      'bindAdDomainToUsers',
      'bindAssociations',
    ]),

    handleAfterDeleted() {
      this.viewport.redirect(this.activeDirectoryRoute);
      this.refreshCollection();
    },

    handleDelete() {
      ModalEventBus.open({
        component: DeleteAdDomainModal,
        componentProps: {
          handleAfterDeleted: this.handleAfterDeleted,
        },
      });
    },

    async handleSave() {
      this.itemHasBeenSaved = true;
      const parentType = activeDirectory;

      return this.bindAdDomainToUsers()
        .then(() => this.bindAssociations({ parentType, targetType: userGroup }))
        .then(() => {
        })
        .catch((error) => {
          throw error instanceof Error ? error : Error('Unknown error');
        });
    },

    /**
     * @todo Consider moving some aspect of this method to the AsidePanel
     * component. Both the RadiusAsidePanel and UserAsidePanel have similar
     * functionality. However, the parent component _will_ need the ability to
     * supplement the base functionality. For example, in addition to checking
     * if the item has changed, the UserAsidePanel checks if the associations
     * or public keys have changed too.
     */
    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }

      return this.haveAssociationsChanged();
    },
  },
};
</script>
